'use client';

import { BaseTranslations } from '@hyphengroup/cms-components/baseTranslations';
import { StickyFooterCta } from '@hyphengroup/cms-components/cms-blocks/StickyFooterCta';
import { MaterialIcon } from '@hyphengroup/cms-components/components/MaterialIcon';
import {
  ProductDetails,
  type ProductDetailsProps,
} from '@hyphengroup/cms-components/components/ProductDetails/ProductDetails';
import {
  ComponentTypeEnum,
  UserActionEnum,
} from '@hyphengroup/cms-components/utils/analytics';
import { getClientTranslations } from '@hyphengroup/cms-components/utils/translations/getClientTranslations';
import { tr } from '@hyphengroup/cms-components/utils/translations/tr';
import classNames from 'classnames';
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useDetectedSiblingSections } from './tiSections';
import { GlobalStickyPanel } from '$/components/cms/components-user/travel/GlobalStickyPanel';
import { useClaimStickyTopSlot } from '$/components/cms/components-user/travel/StickyTopSlot';
import { usePageTiTiers } from '$/components/cms/components-user/travel/useTiTiers';
import { useClientContext } from '$/modules/context/client';

// Approximate pinned two-row nav height (tier row + section row), used for
// scroll-spy / smooth-scroll offset (CMS-1643 AC5/AC6). The nav pins at top:0,
// so scrolling to a section offsets by this height. Refine in /verify.
const TI_NAV_STICKY_OFFSET = 96;

type TierOption = { id: string; name: string };

/**
 * Nav tier row (Figma viewport-scroll): "Viewing details for :product-tier" on
 * the left + a boxed tier switcher on the right. Single-tier trip type → static
 * label, no switcher (CMS-1643 AC-14). Hidden while there is no effective tier.
 */
const TiTierRow = ({
  tiers,
  selectedTierId,
  activeTierName,
  onSelect,
  staticText,
}: {
  tiers: TierOption[];
  selectedTierId: string | undefined;
  activeTierName: string | undefined;
  onSelect: (id: string) => void;
  staticText: Record<string, string>;
}): JSX.Element | null => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Dismiss the switcher on outside-click / Escape (Escape restores focus to the
  // trigger). Client-only listeners, so no SSR/hydration impact. The menu is
  // otherwise keyboard-operable via Tab/Enter on its native buttons.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  if (!activeTierName) return null;

  const label = tr('pd_nav_viewing_details', staticText, {
    'product-tier': activeTierName,
  });

  return (
    <div className="flex items-center justify-between gap-2 border-b border-borders/default py-spacing/padding/y-sm">
      <span className="mhg-alpha-typography-body-2 min-w-0 truncate text-text/secondary-lighter">
        {label}
      </span>
      {tiers.length > 1 && (
        <div ref={containerRef} className="relative shrink-0">
          <button
            ref={triggerRef}
            type="button"
            aria-haspopup="menu"
            aria-expanded={open}
            onClick={() => setOpen((prev) => !prev)}
            className="mhg-alpha-tag-font flex items-center gap-spacing/gap/soft-8px rounded-sm border border-borders/default px-spacing/padding/x py-spacing/padding/y-xs text-text/primary"
          >
            {activeTierName}
            <MaterialIcon
              icon="expand_less"
              className={classNames('transition-transform', {
                'rotate-0': open,
                'rotate-180': !open,
              })}
            />
          </button>
          {open && (
            <ul
              role="menu"
              // Same left edge and width as the trigger (`inset-x-0` inside the
              // `relative` wrapper) so field and menu read as one box (CMS-1991 QA).
              className="absolute inset-x-0 top-full z-10 overflow-hidden rounded-sm border border-borders/default bg-fill/alternative-1"
            >
              {tiers.map((tier) => (
                <li key={tier.id} role="none">
                  <button
                    type="button"
                    role="menuitemradio"
                    aria-checked={tier.id === selectedTierId}
                    className="mhg-alpha-tag-font w-full px-spacing/padding/x py-spacing/padding/y text-left"
                    onClick={() => {
                      onSelect(tier.id);
                      setOpen(false);
                    }}
                  >
                    {tier.name}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * TI provider page ProductDetails wrapper (CMS-1643 / CMS-1644). Reached from
 * `ProductDetailsBlock` when `renderingMode === 'TI_PROVIDER'`. Brand-gated
 * (SS/MH); locates the visible M3b `PlanTierSelectorComponent` on the page (the
 * tier list source — absent ⇒ renders nothing, documented coupling), resolves the
 * active tier's product via `usePageTiTiers`, and feeds it to the presentational
 * cms-components `ProductDetails` in TI mode. Supplies the tier row + the
 * `GlobalStickyPanel`-backed nav container (portal + fixed, so the nav actually
 * pins — CSS `sticky` breaks in this nested layout), the sibling nav links, and
 * the sticky footer. The nav's `onPinnedChange` drives the top-slot claim so M2
 * un-pins when the nav pins (AC-13).
 */
export const TiProductDetailsWrapper = (
  props: ProductDetailsProps,
): JSX.Element | null => {
  const { brand, lang } = useClientContext();
  const staticText = getClientTranslations(BaseTranslations, {
    brand,
    lang,
  }) as Record<string, string>;

  const {
    planTierComponent,
    tiers,
    effectiveTierId,
    activeProduct,
    activeTierName,
    setSelectedTier,
    defaultTierToPin,
    pinDefaultTier,
  } = usePageTiTiers();
  // Pinned here because pre-selection exists only where this block renders;
  // once pinned, the default survives a re-search (CMS-2054, AC 3.5).
  useEffect(() => {
    if (defaultTierToPin) pinDefaultTier(defaultTierToPin);
  }, [defaultTierToPin, pinDefaultTier]);
  const siblingNavigationItems = useDetectedSiblingSections();
  const claimSlot = useClaimStickyTopSlot();
  const [navPinned, setNavPinned] = useState(false);
  // The nav pins via GlobalStickyPanel (portal to body + position: fixed) so it
  // escapes the ancestor overflow/transform/contain that breaks CSS `sticky`.
  // Pinning (a) claims the sticky top slot so M2 suspends (AC-13) and (b) drives
  // the nav's pinned-only styling — the panel background and the desktop fade
  // strip behind the Apply CTA both stay transparent while the nav is in flow.
  const handleNavPinnedChange = useCallback(
    (pinned: boolean) => {
      claimSlot(pinned);
      setNavPinned(pinned);
    },
    [claimSlot],
  );
  const navStickyContainer = useCallback(
    ({ children }: { children: ReactNode }) => (
      <GlobalStickyPanel
        enabled
        offsetPx={0}
        pinnedClassName="bg-fill/alternative-1 product-details-shadow"
        onPinnedChange={handleNavPinnedChange}
      >
        {children}
      </GlobalStickyPanel>
    ),
    [handleNavPinnedChange],
  );

  const { pushDataLayer } = props;

  // CMS-1861 Event 1: Sticky Section Nav · Select (`navigate` / `dropdown` /
  // `select`). Fires when the user switches product variant (tier) from the nav
  // tier switcher, then delegates to the page-wide tier setter. Flat push of
  // exactly the five fields in the spec — `items[]`, `reward_campaign_*` and
  // `item_list_name` are Event-3-only, so this does not go through
  // `sendProductAnalytics`.
  const handleTierSelect = useCallback(
    (id: string) => {
      setSelectedTier(id);
      const index = tiers.findIndex((tier) => tier.id === id);
      const selected = index >= 0 ? tiers[index] : undefined;
      if (selected && pushDataLayer) {
        pushDataLayer({
          event: 'navigate',
          component_type: ComponentTypeEnum.DROPDOWN,
          component_title: `Sticky Section Nav/Product Variant_${selected.name}`,
          action: UserActionEnum.SELECT,
          index,
        });
      }
    },
    [setSelectedTier, tiers, pushDataLayer],
  );

  if (brand !== 'singsaver' && brand !== 'moneyhero') return null;
  // The tier list comes from the M3b component; without it there is nothing to
  // render (CMS-1644 needs the M3b block on the page).
  if (!planTierComponent) return null;

  const providerName = planTierComponent.provider?.providerName ?? undefined;

  const tierSlot =
    tiers.length > 0 ? (
      <TiTierRow
        tiers={tiers.map((tier) => ({ id: tier.id, name: tier.name }))}
        selectedTierId={effectiveTierId}
        activeTierName={activeTierName}
        onSelect={handleTierSelect}
        staticText={staticText}
      />
    ) : undefined;

  return (
    <>
      <ProductDetails
        {...props}
        product={activeProduct}
        vertical="TRAVEL_INSURANCE"
        stickyOffset={TI_NAV_STICKY_OFFSET}
        tierSlot={tierSlot}
        siblingNavigationItems={siblingNavigationItems}
        navStickyContainer={navStickyContainer}
        navPinned={navPinned}
      />
      {/* Bottom spacer so the fixed footer doesn't cover the last section. */}
      {activeProduct && <div aria-hidden className="h-24 md:h-20" />}
      <StickyFooterCta
        product={activeProduct}
        tierName={activeTierName}
        providerName={providerName}
        pushDataLayer={pushDataLayer}
      />
    </>
  );
};
